/**
 * Verify Email Notice Page - Liquid Glass UI
 * Shown to users who haven't verified their email yet
 */
import React, { useState } from 'react';
import { useAuth } from '../components/AuthProvider';
import { resendVerification } from '../services/authService';

const VerifyEmailNotice = () => {
  const { user, logout } = useAuth();
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // success or error

  const handleResend = async () => {
    if (!user?.email) return;

    setResending(true);
    setMessage('');

    try {
      await resendVerification(user.email);
      setMessageType('success');
      setMessage('Verification email sent! Please check your inbox.');
    } catch (error) {
      console.error('Resend error:', error);
      setMessageType('error');
      setMessage(
        error.response?.data?.detail ||
        'Failed to resend email. Please try again later.'
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen liquid-bg flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="liquid-blob blob-1"></div>
        <div className="liquid-blob blob-2"></div>
        <div className="liquid-blob blob-3"></div>
      </div>

      <div className="relative z-10 glass-panel max-w-md w-full p-8 text-center">
        {/* Logo/Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold gradient-text" style={{ fontFamily: "'Poppins', sans-serif" }}>
            CarbonRoute
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Powered by Arantree Consulting Services
          </p>
        </div>

        {/* Email Icon */}
        <div className="py-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-teal-400 to-cyan-600 flex items-center justify-center mb-6 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Verify Your Email
          </h2>
          <p className="text-gray-600">
            We've sent a verification link to:
          </p>
          <p className="text-lg font-medium text-teal-600 mt-2">
            {user?.email}
          </p>
        </div>

        {/* Instructions */}
        <div className="glass-card p-4 text-left mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-2">Next steps:</p>
          <ol className="text-sm text-gray-600 space-y-2">
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-xs font-bold">1</span>
              Check your email inbox
            </li>
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-xs font-bold">2</span>
              Click the verification link
            </li>
            <li className="flex items-center gap-2">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-xs font-bold">3</span>
              Return here and sign in
            </li>
          </ol>
        </div>

        {/* Message */}
        {message && (
          <div className={`glass-alert mb-6 ${
            messageType === 'success' ? 'glass-alert-success' : 'glass-alert-error'
          }`}>
            <p className="text-sm">{message}</p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleResend}
            disabled={resending}
            className="glass-button-secondary w-full py-3 text-sm font-semibold"
          >
            {resending ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </span>
            ) : (
              'Resend Verification Email'
            )}
          </button>

          <button
            onClick={logout}
            className="w-full py-3 px-4 rounded-xl text-sm font-semibold text-gray-600 bg-white/50 hover:bg-white/70 border border-gray-200 transition-all"
          >
            Sign Out
          </button>
        </div>

        {/* Help text */}
        <div className="mt-6 pt-6 border-t border-teal-200/30 text-xs text-gray-500">
          <p>Didn't receive the email? Check your spam folder.</p>
          <p className="mt-1">
            The verification link expires in 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailNotice;
